import { Dialog, DialogContent } from '@/components/ui/dialog';
import { atom, useAtom } from 'jotai';
import { FC, ReactElement, ReactNode, useCallback, useEffect } from 'react';

type DialogItem = {
  id: string;
  content: ReactElement;
  className?: string;
};

type DialogApi = {
  openDialog: (content: ReactElement, className?: string) => void;
  dismissActiveDialog: () => void;
  dismissAllDialogs: () => void;
};

const dialogStackAtom = atom<DialogItem[]>([]);

interface ProviderProps {
  children?: ReactNode;
}

const DialogProvider: FC<ProviderProps> = ({ children }) => {
  const [dialogs, setDialogs] = useAtom(dialogStackAtom);

  const _handleCloseById = (id: string) => setDialogs((prev) => prev.filter((d) => d.id !== id));

  useEffect(() => {
    if (dialogs.length === 0) {
      document.body.style.setProperty('pointer-events', 'auto');
    } else {
      document.body.style.setProperty('pointer-events', 'none');
    }
  }, [dialogs]);

  return (
    <>
      {children}
      {dialogs.map((item) => (
        <Dialog
          data-id={item.id}
          key={item.id}
          open
          onOpenChange={(open) => {
            if (!open) _handleCloseById(item.id);
          }}>
          <DialogContent className={item.className}>{item.content}</DialogContent>
        </Dialog>
      ))}
    </>
  );
};

export const useDialog = (): DialogApi => {
  const [, setDialogs] = useAtom(dialogStackAtom);

  const openDialog = useCallback(
    (content: ReactElement, className?: string, _id?: string) => {
      const id =
        _id ||
        (typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`);
      setDialogs((prev) => [...prev, { id, content, className }]);
    },
    [setDialogs],
  );

  const dismissActiveDialog = useCallback(
    () => setDialogs((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev)),
    [setDialogs],
  );

  const dismissAllDialogs = useCallback(() => setDialogs([]), [setDialogs]);

  return { openDialog, dismissActiveDialog, dismissAllDialogs };
};

export default DialogProvider;
